import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const SectionTitle = styled.h2`
  margin-bottom: 1.5rem;
  color: white;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 2fr auto auto;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
  
  option {
    background: #2a5298;
    color: white;
    padding: 0.5rem;
    font-weight: 500;
  }
  
  option:hover {
    background: #667eea;
  }
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const JarSection = styled.div`
  margin-bottom: 2rem;
`;

const JarTitle = styled.h3`
  margin-bottom: 1rem;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
`;

const SubheadingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 300px;
  overflow-y: auto;
`;

const SubheadingItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SubheadingText = styled.div`
  flex: 1;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const SubheadingMeta = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
`;

const DeleteButton = styled.button`
  padding: 0.5rem 1rem;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #c0392b;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.6);
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function SubheadingManagement({ showMessage }) {
  const [jars, setJars] = useState([]);
  const [subheadings, setSubheadings] = useState([]);
  const [form, setForm] = useState({
    jar_id: '',
    subtitle_text: '',
    display_order: 0,
    status: 'active'
  });
  const [loading, setLoading] = useState(false);

  // Helper function to get main title (same as JarManagement)
  const getMainTitle = (titles) => {
    if (!titles || titles.length === 0) return 'Untitled';
    const mainTitle = titles.find(t => t.title_type === 'main');
    return mainTitle ? mainTitle.title : titles[0].title;
  };

  const fetchJars = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/jars`);
      const data = await response.json();
      if (data.success) {
        setJars(data.data.filter(jar => jar.status === 'active'));
      }
    } catch (error) {
      showMessage('Error fetching jars', 'error');
    }
  }, [showMessage]);

  const fetchSubheadings = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/jars`);
      const data = await response.json();
      if (data.success) {
        // Group subheadings by jar
        const jarsWithSubheadings = data.data;
        setSubheadings(jarsWithSubheadings);
      }
    } catch (error) {
      showMessage('Error fetching subheadings', 'error');
    }
  }, [showMessage]);

  useEffect(() => {
    fetchJars();
    fetchSubheadings();
  }, [fetchJars, fetchSubheadings]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/jars/subheadings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(form)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('Subheading created successfully');
        setForm({ jar_id: '', subtitle_text: '', display_order: 0, status: 'active' });
        fetchSubheadings();
      } else {
        showMessage(data.message || 'Error creating subheading', 'error');
      }
    } catch (error) {
      showMessage('Network error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteSubheading = async (subheadingId) => {
    if (!window.confirm('Are you sure you want to delete this subheading?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/jars/subheadings/${subheadingId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('Subheading deleted successfully');
        fetchSubheadings();
      } else {
        showMessage(data.message || 'Error deleting subheading', 'error');
      }
    } catch (error) {
      showMessage('Network error', 'error');
    }
  };

  // Group subheadings by jar for display
  const subheadingsByJar = subheadings.reduce((acc, jar) => {
    // Always add jar to the list, even if no subheadings
    acc[jar.id] = {
      jarTitle: getMainTitle(jar.titles), // Use getMainTitle instead of jar.title
      subheadings: jar.subheadings || [] // Default to empty array if no subheadings
    };
    
    return acc;
  }, {});

  return (
    <Container>
      <SectionTitle>Subheadings Management</SectionTitle>
      
      <Form onSubmit={handleSubmit}>
        <Select
          value={form.jar_id}
          onChange={(e) => setForm({...form, jar_id: e.target.value})}
          required
        >
          <option value="">Select Jar</option>
          {jars.map(jar => {
            const jarTitle = getMainTitle(jar.titles);
            return (
              <option key={jar.id} value={jar.id}>
                {jarTitle}
              </option>
            );
          })}
        </Select>
        
        <Input
          type="text"
          placeholder="Subheading Text"
          value={form.subtitle_text}
          onChange={(e) => setForm({...form, subtitle_text: e.target.value})}
          required
        />
        
        <Input
          type="number"
          placeholder="Order"
          value={form.display_order}
          onChange={(e) => setForm({...form, display_order: parseInt(e.target.value)})}
          min="0"
          style={{ maxWidth: '100px' }}
        />
        
        <Button type="submit" disabled={loading}>
          {loading ? 'Adding...' : 'Add Subheading'}
        </Button>
      </Form>

      {Object.keys(subheadingsByJar).length > 0 ? (
        Object.entries(subheadingsByJar).map(([jarId, jarData]) => (
          <JarSection key={jarId}>
            <JarTitle>{jarData.jarTitle}</JarTitle>
            <SubheadingList>
              {jarData.subheadings.length > 0 ? (
                jarData.subheadings.map(subheading => (
                  <SubheadingItem key={subheading.id}>
                    <div>
                      <SubheadingText>{subheading.subtitle_text}</SubheadingText>
                      <SubheadingMeta>
                        Order: {subheading.display_order} | Status: {subheading.status}
                      </SubheadingMeta>
                    </div>
                    <DeleteButton onClick={() => deleteSubheading(subheading.id)}>
                      Delete
                    </DeleteButton>
                  </SubheadingItem>
                ))
              ) : (
                <EmptyState>
                  No subheadings found for this jar. Add your first subheading above.
                </EmptyState>
              )}
            </SubheadingList>
          </JarSection>
        ))
      ) : (
        <EmptyState>
          No subheadings found. Create your first subheading above.
        </EmptyState>
      )}
    </Container>
  );
}

export default SubheadingManagement;
